// Selection-marker visual + per-frame animator. The marker is an accent core
// dot (built in setup.ts) plus ONE billboarded two-tone outline ring: a dark
// band beside a light band, so the marker reads on both black and white
// backgrounds. (The old design used an additive-white halo, which vanished on
// a white background, plus two stacked ring sprites that coincided.)
//
// Phase state (hover/pulse) is module-local: there is one selection marker per
// session, matching the @scene/picking/state singleton convention. Only
// interactions.ts / cursor.ts write the phase; only loop.ts ticks it.

import * as THREE from 'three';
import { CAMERA_DEFAULTS } from '@config/defaults';
import type { ThemeSelection } from '@config/defaults';
import type { SceneCamera } from '@app/context';

// Zoom-aware sizing. OrbitControls zooms by dollying the camera, so a
// fixed-world-size marker reads huge up close and a speck far out. We scale the
// core mesh per frame by a damped function of camera distance so the apparent
// size stays comfortable: world scale k = (dist / REF) ^ ZOOM_DAMP, clamped.
// ZOOM_DAMP 0 = world-fixed (old behavior), 1 = constant on-screen; 0.7 grows a
// little zoomed in, shrinks a little zoomed out. REF = the default framing
// distance, so k = 1 there and the marker matches its built size.
const REFERENCE_VIEW_DISTANCE = Math.hypot(
    CAMERA_DEFAULTS.position.x - CAMERA_DEFAULTS.target.x,
    CAMERA_DEFAULTS.position.y - CAMERA_DEFAULTS.target.y,
    CAMERA_DEFAULTS.position.z - CAMERA_DEFAULTS.target.z,
);
const ZOOM_DAMP = 0.7;
const ZOOM_SCALE_MIN = 0.5;
const ZOOM_SCALE_MAX = 3.5;
// Orthographic counterpart: apparent size is governed by camera.zoom, not
// distance. zoom = 1 is the default framing (matches the default perspective
// distance), so the designed marker size lines up across a projection swap.
const REFERENCE_ZOOM = 1;

// Code defaults for the optional theme knobs.
const DEFAULT_OUTLINE_SCALE = 3.2; // ring sprite size ÷ sphere radius
const DEFAULT_HOVER_SCALE = 0.15; // extra ring scale at full hover
const DEFAULT_HOVER_BRIGHTEN = 0.4; // core lerp-to-white at full hover (0..1)
const DEFAULT_PULSE_SCALE = 0.35; // extra ring scale at the click-pulse peak
const OUTLINE_BASE_OPACITY = 1.0; // multiplied by theme glowOpacity

// Drag handles. Two billboarded knobs on stems: the up/down (↕) knob is always
// available where vertical drag works; the horizontal (↔) knob appears only in
// the near-equatorial regime, pointed along the one cross-screen world axis (see
// interactions.ts). Touch-friendly affordances for the same axis-locked motions.
const DEFAULT_HANDLE_OFFSET_SCALE = 8.5; // knob distance from core ÷ sphere radius
const DEFAULT_HANDLE_SCALE = 2.7; // knob sprite size ÷ sphere radius
const DEFAULT_HANDLE_IDLE_OPACITY = 0.4; // ↕ knob/stem opacity at rest (~20% subtler)
const DEFAULT_HHANDLE_IDLE_OPACITY = 0.3; // ↔ knob/stem opacity at rest (subtler still)
const HANDLE_RENDER_ORDER = 999; // draw the affordance on top, like a gizmo

// Drag-axis gating by camera elevation θ above the horizontal plane (0° edge-on,
// 90° top-down). Vertical drag (ray vs world-Y, ∝ cos²θ) degenerates near 90°;
// horizontal-plane drag (raycast onto y=0, ∝ 1/sinθ) degenerates near 0°.
export const VERTICAL_MAX_ELEV_DEG = 60; // above this, up/down is disabled (↕ knob fades)
export const HORIZONTAL_MIN_ELEV_DEG = 15; // below this, the free X/Y plane gives way to
                                           // vertical + a single cross-screen axis (↔ stem)
// Azimuth window: when the horizontal view direction is within this many degrees
// of a world axis, the *other* horizontal axis is cleanly cross-screen and gets
// the ↔ stem. The ~45° diagonal (beyond this) is ambiguous → no ↔ stem.
export const AZIMUTH_ALIGN_MAX_DEG = 40;

const HOVER_RATE = 14; // easing rate (1/s) toward the hover target
const PULSE_RATE = 7; // decay rate (1/s) for the click pulse
const ACTIVE_RATE = 12; // easing rate (1/s) for the drag-hold rise/release
const ACTIVE_HOLD_FRACTION = 0.5; // held expansion ÷ pulse peak while dragging
const HANDLE_RATE = 14; // easing rate (1/s) for handle hover/active
const HANDLE_HOVER_GROW = 0.25; // extra knob scale at full hover/active
const DT_CLAMP = 0.1; // cap dt so a tab-switch stall doesn't snap the anim

const numOr = (v: number | null | undefined, d: number): number => (typeof v === 'number' ? v : d);

interface MarkerHandles {
    outline: THREE.Sprite;
    core: THREE.Mesh;
    coreBaseColor: THREE.Color; // hover lerps toward white from this
    white: THREE.Color;
    outlineBaseScale: number; // world units
    hoverScale: number;
    hoverBrighten: number;
    pulseScale: number;
    // Vertical (↕) drag handle (null when handle:false / glow:false).
    knob: THREE.Sprite | null;
    stem: THREE.Line | null;
    knobBaseScale: number; // world units
    knobBaseColor: THREE.Color; // brightens toward white on hover/active
    handleIdleOpacity: number;
    handleOffset: number; // stem length / knob distance, world units (shared by both handles)
    // Horizontal (↔) drag handle, reoriented per frame along world X or Z.
    hKnob: THREE.Sprite | null;
    hStem: THREE.Line | null;
    hKnobBaseScale: number; // world units
    hHandleIdleOpacity: number;
}

let _m: MarkerHandles | null = null;
let _hover = 0;
let _hoverTarget = 0;
let _pulse = 0;
let _active = 0;
let _activeTarget = 0;
let _handleHover = 0;
let _handleHoverTarget = 0;
let _handleActive = 0;
let _handleActiveTarget = 0;
// Eased enable gate (1 = up/down available, 0 = disabled at this view angle): the
// knob fades out rather than popping as θ crosses VERTICAL_MAX_ELEV_DEG.
let _handleEnabled = 1;
let _handleEnabledTarget = 1;
// Horizontal (↔) handle phase + enable gate (starts hidden — only shown in the
// near-equatorial, axis-aligned regime). `_hAxis` caches the current orientation
// so the stem geometry is only rewritten when the cross-screen axis flips.
let _hHover = 0;
let _hHoverTarget = 0;
let _hActive = 0;
let _hActiveTarget = 0;
let _hEnabled = 0;
let _hEnabledTarget = 0;
let _hAxis: 'x' | 'z' | null = null;
const _viewDir = new THREE.Vector3();

/** Paint a two-tone outline ring into a power-of-two canvas: an inner dark band
 *  adjacent to an outer light band. Strokes are anti-aliased by the 2D context. */
function paintOutlineTexture(): THREE.CanvasTexture {
    const SIZE = 256;
    const c = document.createElement('canvas');
    c.width = SIZE;
    c.height = SIZE;
    const ctx = c.getContext('2d');
    if (ctx) {
        const cx = SIZE / 2;
        const band = 16;
        // Outer (light) and inner (dark) concentric rims, adjacent.
        ctx.lineWidth = band;
        ctx.strokeStyle = 'rgba(255,255,255,0.95)';
        ctx.beginPath();
        ctx.arc(cx, cx, 102, 0, Math.PI * 2);
        ctx.stroke();
        ctx.strokeStyle = 'rgba(0,0,0,0.85)';
        ctx.beginPath();
        ctx.arc(cx, cx, 86, 0, Math.PI * 2);
        ctx.stroke();
    }
    const tex = new THREE.CanvasTexture(c);
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.needsUpdate = true;
    return tex;
}

/** Paint the up/down knob: a uniform light disc (no rim) carrying a bold dark ↕
 *  double-chevron — the prominent "move vertically" affordance. */
function paintKnobTexture(): THREE.CanvasTexture {
    const SIZE = 128;
    const c = document.createElement('canvas');
    c.width = SIZE;
    c.height = SIZE;
    const ctx = c.getContext('2d');
    if (ctx) {
        const cx = SIZE / 2;
        // Uniform disc, edge-free.
        ctx.fillStyle = 'rgba(255,255,255,0.96)';
        ctx.beginPath();
        ctx.arc(cx, cx, 58, 0, Math.PI * 2);
        ctx.fill();
        // ↕ double chevron — large, thick, high-contrast ink so it reads at a glance.
        ctx.strokeStyle = 'rgba(0,0,0,0.95)';
        ctx.lineWidth = 14;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(cx - 24, 52); ctx.lineTo(cx, 24); ctx.lineTo(cx + 24, 52); // up
        ctx.moveTo(cx - 24, 76); ctx.lineTo(cx, 104); ctx.lineTo(cx + 24, 76); // down
        ctx.stroke();
    }
    const tex = new THREE.CanvasTexture(c);
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.needsUpdate = true;
    return tex;
}

/** Paint the left/right knob: the ↕ disc with a `<>` double chevron (the vertical
 *  chevrons transposed) — the "move along this horizontal axis" affordance. */
function paintKnobTextureH(): THREE.CanvasTexture {
    const SIZE = 128;
    const c = document.createElement('canvas');
    c.width = SIZE;
    c.height = SIZE;
    const ctx = c.getContext('2d');
    if (ctx) {
        const cy = SIZE / 2;
        ctx.fillStyle = 'rgba(255,255,255,0.96)';
        ctx.beginPath();
        ctx.arc(cy, cy, 58, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,0.95)';
        ctx.lineWidth = 14;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(52, cy - 24); ctx.lineTo(24, cy); ctx.lineTo(52, cy + 24); // left
        ctx.moveTo(76, cy - 24); ctx.lineTo(104, cy); ctx.lineTo(76, cy + 24); // right
        ctx.stroke();
    }
    const tex = new THREE.CanvasTexture(c);
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.needsUpdate = true;
    return tex;
}

/**
 * Build the outline ring as a child-able sprite and register the marker for
 * per-frame animation. `core` is the accent dot mesh (owned by setup.ts) whose
 * material brightness the animator lerps on hover. Idempotent: re-building
 * disposes the previous outline texture/material.
 */
export function buildSelectionMarker(
    core: THREE.Mesh,
    sphereRadius: number,
    selTheme: ThemeSelection | null | undefined,
): THREE.Sprite {
    disposeMarker();

    const glowOpacity = numOr(selTheme?.glowOpacity, 1.0);
    const tex = paintOutlineTexture();
    const mat = new THREE.SpriteMaterial({
        map: tex,
        transparent: true,
        depthTest: true,
        depthWrite: false,
        opacity: OUTLINE_BASE_OPACITY * glowOpacity,
    });
    const outline = new THREE.Sprite(mat);
    outline.name = 'selectionOutline';
    const outlineBaseScale = sphereRadius * numOr(selTheme?.outlineScale, DEFAULT_OUTLINE_SCALE);
    outline.scale.set(outlineBaseScale, outlineBaseScale, 1);

    // Vertical drag handle: a thin stem + a billboarded ↕ knob, both children of
    // the core so they track every marker move. depthTest:false + a high render
    // order keep them readable on top, like a transform gizmo. `handle:false`
    // opts out (the handle also requires the marker, i.e. `glow !== false`).
    const handleEnabled = selTheme?.handle !== false;
    const handleIdleOpacity = numOr(selTheme?.handleIdleOpacity, DEFAULT_HANDLE_IDLE_OPACITY);
    const hHandleIdleOpacity = numOr(selTheme?.hHandleIdleOpacity, DEFAULT_HHANDLE_IDLE_OPACITY);
    let knob: THREE.Sprite | null = null;
    let stem: THREE.Line | null = null;
    let knobBaseScale = 0;
    let hKnob: THREE.Sprite | null = null;
    let hStem: THREE.Line | null = null;
    let hKnobBaseScale = 0;
    const offset = sphereRadius * numOr(selTheme?.handleOffsetScale, DEFAULT_HANDLE_OFFSET_SCALE);
    if (handleEnabled) {
        const stemGeom = new THREE.BufferGeometry();
        stemGeom.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0, 0, offset, 0], 3));
        const stemMat = new THREE.LineBasicMaterial({
            color: 0xffffff, transparent: true, opacity: handleIdleOpacity, depthTest: false, depthWrite: false,
        });
        stem = new THREE.Line(stemGeom, stemMat);
        stem.name = 'selectionHandleStem';
        stem.frustumCulled = false;
        stem.renderOrder = HANDLE_RENDER_ORDER;
        core.add(stem);

        const knobMat = new THREE.SpriteMaterial({
            map: paintKnobTexture(), transparent: true, depthTest: false, depthWrite: false, opacity: handleIdleOpacity,
        });
        knob = new THREE.Sprite(knobMat);
        knob.name = 'selectionHandleKnob';
        knobBaseScale = sphereRadius * numOr(selTheme?.handleScale, DEFAULT_HANDLE_SCALE);
        knob.scale.set(knobBaseScale, knobBaseScale, 1);
        knob.position.set(0, offset, 0);
        knob.renderOrder = HANDLE_RENDER_ORDER;
        core.add(knob);
        _handleEnabled = 1;
        _handleEnabledTarget = 1;

        // Horizontal (↔) handle: same build, initial endpoint along +X. The real
        // axis (X or Z) + visibility are set every frame by applyHandleOrientation;
        // it starts hidden (_hEnabled = 0) and only appears in the equatorial regime.
        const hStemGeom = new THREE.BufferGeometry();
        hStemGeom.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0, offset, 0, 0], 3));
        const hStemMat = new THREE.LineBasicMaterial({
            color: 0xffffff, transparent: true, opacity: hHandleIdleOpacity, depthTest: false, depthWrite: false,
        });
        hStem = new THREE.Line(hStemGeom, hStemMat);
        hStem.name = 'selectionHHandleStem';
        hStem.frustumCulled = false;
        hStem.renderOrder = HANDLE_RENDER_ORDER;
        hStem.visible = false;
        core.add(hStem);

        const hKnobMat = new THREE.SpriteMaterial({
            map: paintKnobTextureH(), transparent: true, depthTest: false, depthWrite: false, opacity: hHandleIdleOpacity,
        });
        hKnob = new THREE.Sprite(hKnobMat);
        hKnob.name = 'selectionHHandleKnob';
        hKnobBaseScale = sphereRadius * numOr(selTheme?.handleScale, DEFAULT_HANDLE_SCALE);
        hKnob.scale.set(hKnobBaseScale, hKnobBaseScale, 1);
        hKnob.position.set(offset, 0, 0);
        hKnob.renderOrder = HANDLE_RENDER_ORDER;
        hKnob.visible = false;
        core.add(hKnob);
        _hEnabled = 0;
        _hEnabledTarget = 0;
        _hAxis = null;
    }

    const coreMat = (Array.isArray(core.material) ? core.material[0] : core.material) as THREE.MeshBasicMaterial;
    _m = {
        outline,
        core,
        coreBaseColor: coreMat.color.clone(),
        white: new THREE.Color(0xffffff),
        outlineBaseScale,
        hoverScale: numOr(selTheme?.hoverScale, DEFAULT_HOVER_SCALE),
        hoverBrighten: numOr(selTheme?.hoverBrighten, DEFAULT_HOVER_BRIGHTEN),
        pulseScale: numOr(selTheme?.pulseScale, DEFAULT_PULSE_SCALE),
        knob,
        stem,
        knobBaseScale,
        knobBaseColor: new THREE.Color(0xffffff),
        handleIdleOpacity,
        handleOffset: offset,
        hKnob,
        hStem,
        hKnobBaseScale,
        hHandleIdleOpacity,
    };
    return outline;
}

/** Hover target (0/1). Safe before the marker is built (no-op until ticked). */
export function setMarkerHover(on: boolean): void {
    _hoverTarget = on ? 1 : 0;
}

/** Kick a decaying click pulse. */
export function triggerMarkerPulse(): void {
    _pulse = 1;
}

/** Hold the ring partly expanded while dragging (0/1); released on drop, where
 *  it eases back down — the tail of the click-shrink. */
export function setMarkerActive(on: boolean): void {
    _activeTarget = on ? 1 : 0;
}

/** Hover target for the vertical drag knob (0/1). */
export function setHandleHover(on: boolean): void {
    _handleHoverTarget = on ? 1 : 0;
}

/** Held-bright state while dragging the marker vertically via the knob (0/1). */
export function setHandleActive(on: boolean): void {
    _handleActiveTarget = on ? 1 : 0;
}

/** Hover target for the horizontal (↔) drag knob (0/1). */
export function setHHandleHover(on: boolean): void {
    _hHoverTarget = on ? 1 : 0;
}

/** Held-bright state while dragging the marker along the horizontal axis (0/1). */
export function setHHandleActive(on: boolean): void {
    _hActiveTarget = on ? 1 : 0;
}

/** Copy the ↕ knob's world position into `out`; returns false when no handle
 *  exists, so the pointer code can hit-test without owning the object. */
export function getHandleWorldPosition(out: THREE.Vector3): boolean {
    if (!_m || !_m.knob) return false;
    _m.knob.getWorldPosition(out);
    return true;
}

/** Copy the ↔ knob's world position into `out`; false when no handle exists. */
export function getHorizontalHandleWorldPosition(out: THREE.Vector3): boolean {
    if (!_m || !_m.hKnob) return false;
    _m.hKnob.getWorldPosition(out);
    return true;
}

/** Camera elevation above the horizontal plane in degrees: 0° = looking edge-on,
 *  90° = straight down/up. Reuses a module scratch vec (no allocation). */
export function cameraElevationDeg(camera: SceneCamera): number {
    camera.getWorldDirection(_viewDir);
    return Math.asin(Math.min(1, Math.abs(_viewDir.y))) * (180 / Math.PI);
}

/** Whether vertical (up/down) drag is usable at the current view angle. */
export function verticalDragAllowed(camera: SceneCamera): boolean {
    return cameraElevationDeg(camera) <= VERTICAL_MAX_ELEV_DEG;
}

/** Whether the free X/Y plane drag is usable at the current view angle. Below
 *  this, the equatorial regime (vertical + single cross-screen axis) takes over. */
export function horizontalDragAllowed(camera: SceneCamera): boolean {
    return cameraElevationDeg(camera) >= HORIZONTAL_MIN_ELEV_DEG;
}

/** The single cross-screen horizontal world axis to drag along in the equatorial
 *  regime, or null when the view is too top-down to read or sits in the ~45°
 *  diagonal band (ambiguous). 'x'/'z' name the world axis. Reuses `_viewDir`. */
export function horizontalDragAxis(camera: SceneCamera): 'x' | 'z' | null {
    camera.getWorldDirection(_viewDir);
    const ax = Math.abs(_viewDir.x);
    const az = Math.abs(_viewDir.z);
    const hi = Math.max(ax, az);
    if (hi < 1e-4) return null; // looking straight up/down — no horizontal axis
    const phi = Math.atan2(Math.min(ax, az), hi) * (180 / Math.PI);
    if (phi > AZIMUTH_ALIGN_MAX_DEG) return null; // ~45° diagonal: neither axis is clearly cross-screen
    // Drag the smaller-component (cross-screen) axis; the larger one is into-screen.
    return ax >= az ? 'z' : 'x';
}

/** Per-frame: fade the ↕ knob to the current view angle, and orient/fade the ↔
 *  knob to the equatorial cross-screen axis. Must run every frame (OrbitControls
 *  damping keeps the camera gliding after release). */
export function applyHandleOrientation(camera: SceneCamera): void {
    const m = _m;
    if (!m) return;
    if (m.knob) _handleEnabledTarget = verticalDragAllowed(camera) ? 1 : 0;
    if (m.hKnob && m.hStem) {
        const equatorial = cameraElevationDeg(camera) < HORIZONTAL_MIN_ELEV_DEG;
        const axis = equatorial ? horizontalDragAxis(camera) : null;
        _hEnabledTarget = axis ? 1 : 0;
        if (axis && axis !== _hAxis) {
            const o = m.handleOffset;
            const pos = m.hStem.geometry.getAttribute('position') as THREE.BufferAttribute;
            if (axis === 'x') {
                pos.setXYZ(1, o, 0, 0);
                m.hKnob.position.set(o, 0, 0);
            } else {
                pos.setXYZ(1, 0, 0, o);
                m.hKnob.position.set(0, 0, o);
            }
            pos.needsUpdate = true;
            _hAxis = axis;
        }
    }
}

/**
 * Per-frame animator. Frame-rate-independent easing via `1 - exp(-dt*rate)`;
 * eases the outline scale (in place) and the core material color. Also sets the
 * core mesh scale to a slow zoom-tracking factor (see REFERENCE_VIEW_DISTANCE)
 * so the marker holds a comfortable apparent size at any zoom — the hit-test in
 * interactions.ts reads `cubeCenterDot.scale.x`, so picking stays in lockstep.
 * The hover/pulse easing stays local to the outline sprite (never the core
 * scale) and rides on top of this parent scale.
 */
export function updateMarker(dt: number, camera: SceneCamera): void {
    const m = _m;
    if (!m) return;
    if (dt > DT_CLAMP) dt = DT_CLAMP;

    // Zoom-aware world scale on the core (parents the whole assembly). core is a
    // scene-root child, so its local position is its world position. Perspective
    // keys off camera distance; orthographic keys off camera.zoom (distance is
    // irrelevant to apparent size there).
    let zoomK: number;
    if (camera instanceof THREE.OrthographicCamera) {
        zoomK = (REFERENCE_ZOOM / camera.zoom) ** ZOOM_DAMP;
    } else {
        const dist = camera.position.distanceTo(m.core.position);
        zoomK = (dist / REFERENCE_VIEW_DISTANCE) ** ZOOM_DAMP;
    }
    zoomK = Math.min(ZOOM_SCALE_MAX, Math.max(ZOOM_SCALE_MIN, zoomK));
    m.core.scale.setScalar(zoomK);

    _hover += (_hoverTarget - _hover) * (1 - Math.exp(-dt * HOVER_RATE));
    _pulse += (0 - _pulse) * (1 - Math.exp(-dt * PULSE_RATE));
    _active += (_activeTarget - _active) * (1 - Math.exp(-dt * ACTIVE_RATE));

    const expand = _pulse + ACTIVE_HOLD_FRACTION * _active;
    const scale = m.outlineBaseScale * (1 + m.hoverScale * _hover + m.pulseScale * expand);
    m.outline.scale.set(scale, scale, 1);

    const t = Math.min(1, m.hoverBrighten * _hover + 0.5 * m.pulseScale * expand);
    const mat = (Array.isArray(m.core.material) ? m.core.material[0] : m.core.material) as THREE.MeshBasicMaterial;
    mat.color.copy(m.coreBaseColor).lerp(m.white, t);

    // Vertical handle: ease hover/active + the view-angle enable gate, then
    // brighten/grow the knob and fade it out when up/down is disabled.
    if (m.knob && m.stem) {
        _handleHover += (_handleHoverTarget - _handleHover) * (1 - Math.exp(-dt * HANDLE_RATE));
        _handleActive += (_handleActiveTarget - _handleActive) * (1 - Math.exp(-dt * HANDLE_RATE));
        _handleEnabled += (_handleEnabledTarget - _handleEnabled) * (1 - Math.exp(-dt * HANDLE_RATE));
        const lit = Math.max(_handleHover, _handleActive);
        const op = (m.handleIdleOpacity + (1 - m.handleIdleOpacity) * lit) * _handleEnabled;
        const knobMat = m.knob.material as THREE.SpriteMaterial;
        knobMat.opacity = op;
        (m.stem.material as THREE.LineBasicMaterial).opacity = op;
        const ks = m.knobBaseScale * (1 + HANDLE_HOVER_GROW * lit);
        m.knob.scale.set(ks, ks, 1);
        const shown = _handleEnabled > 0.02;
        m.knob.visible = shown;
        m.stem.visible = shown;
    }

    // Horizontal handle: same easing, gated by the equatorial-axis enable.
    if (m.hKnob && m.hStem) {
        _hHover += (_hHoverTarget - _hHover) * (1 - Math.exp(-dt * HANDLE_RATE));
        _hActive += (_hActiveTarget - _hActive) * (1 - Math.exp(-dt * HANDLE_RATE));
        _hEnabled += (_hEnabledTarget - _hEnabled) * (1 - Math.exp(-dt * HANDLE_RATE));
        const lit = Math.max(_hHover, _hActive);
        const op = (m.hHandleIdleOpacity + (1 - m.hHandleIdleOpacity) * lit) * _hEnabled;
        (m.hKnob.material as THREE.SpriteMaterial).opacity = op;
        (m.hStem.material as THREE.LineBasicMaterial).opacity = op;
        const ks = m.hKnobBaseScale * (1 + HANDLE_HOVER_GROW * lit);
        m.hKnob.scale.set(ks, ks, 1);
        const shown = _hEnabled > 0.02;
        m.hKnob.visible = shown;
        m.hStem.visible = shown;
    }
}

/**
 * Re-apply theme to the marker: outline opacity tracks `glowOpacity`, the
 * tunables refresh, and the core base color is re-snapshotted (selection.ts
 * sets the core material color immediately before calling this). The dark/light
 * bands are baked for contrast, so no recolor of the ring texture is needed.
 */
export function applyMarkerTheme(selTheme: ThemeSelection | null | undefined): void {
    const m = _m;
    if (!m) return;
    const omat = m.outline.material as THREE.SpriteMaterial;
    omat.opacity = OUTLINE_BASE_OPACITY * numOr(selTheme?.glowOpacity, 1.0);
    omat.needsUpdate = true;

    const coreMat = (Array.isArray(m.core.material) ? m.core.material[0] : m.core.material) as THREE.MeshBasicMaterial;
    m.coreBaseColor.copy(coreMat.color);
    m.hoverScale = numOr(selTheme?.hoverScale, DEFAULT_HOVER_SCALE);
    m.hoverBrighten = numOr(selTheme?.hoverBrighten, DEFAULT_HOVER_BRIGHTEN);
    m.pulseScale = numOr(selTheme?.pulseScale, DEFAULT_PULSE_SCALE);

    // Handle idle opacities track the theme; updateMarker re-applies them next frame.
    m.handleIdleOpacity = numOr(selTheme?.handleIdleOpacity, DEFAULT_HANDLE_IDLE_OPACITY);
    m.hHandleIdleOpacity = numOr(selTheme?.hHandleIdleOpacity, DEFAULT_HHANDLE_IDLE_OPACITY);
}

/** Dispose the outline + handle textures/materials and clear marker state. */
export function disposeMarker(): void {
    if (!_m) return;
    const mat = _m.outline.material as THREE.SpriteMaterial;
    if (mat.map) mat.map.dispose();
    mat.dispose();
    if (_m.knob) {
        _m.knob.removeFromParent();
        const km = _m.knob.material as THREE.SpriteMaterial;
        if (km.map) km.map.dispose();
        km.dispose();
    }
    if (_m.stem) {
        _m.stem.removeFromParent();
        _m.stem.geometry.dispose();
        (_m.stem.material as THREE.LineBasicMaterial).dispose();
    }
    if (_m.hKnob) {
        _m.hKnob.removeFromParent();
        const hkm = _m.hKnob.material as THREE.SpriteMaterial;
        if (hkm.map) hkm.map.dispose();
        hkm.dispose();
    }
    if (_m.hStem) {
        _m.hStem.removeFromParent();
        _m.hStem.geometry.dispose();
        (_m.hStem.material as THREE.LineBasicMaterial).dispose();
    }
    _m = null;
    _hover = 0;
    _hoverTarget = 0;
    _pulse = 0;
    _active = 0;
    _activeTarget = 0;
    _handleHover = 0;
    _handleHoverTarget = 0;
    _handleActive = 0;
    _handleActiveTarget = 0;
    _handleEnabled = 1;
    _handleEnabledTarget = 1;
    _hHover = 0;
    _hHoverTarget = 0;
    _hActive = 0;
    _hActiveTarget = 0;
    _hEnabled = 0;
    _hEnabledTarget = 0;
    _hAxis = null;
}
